import React from 'react';
import "../../styles/Css/Legal.css";

const PrivacyPolicy = () => {
    const lastUpdated = "March 03rd, 2026";

    const navItems = [
        { id: "info-collect", label: "1. Information We Collect" },
        { id: "how-use", label: "2. How We Use Information" },
        { id: "api-compliance", label: "3. API Compliance" },
        { id: "data-sharing", label: "4. Data Sharing" },
        { id: "data-retention", label: "5. Data Retention" },
        { id: "security", label: "6. Security Measures" },
        { id: "user-rights", label: "7. User Rights" },
        { id: "intl-transfers", label: "8. International Transfers" },
        { id: "policy-changes", label: "9. Changes to Policy" },
        { id: "contact", label: "10. Contact Us" },
    ];

    return (
        <div className="legal-page">
            {/* Hero Header */}
            <section className="legal-hero">
                <div className="container">
                    <div className="legal-badge">Trust & Transparency</div>
                    <h1 className="legal-hero__title">Privacy Policy</h1>
                    <h2 className="legal-hero__subtitle">Deal To Close – AI-Powered Real Estate</h2>
                    <p className="legal-hero__date">Last Updated: {lastUpdated}</p>
                </div>
            </section>

            <section className="legal-content section">
                <div className="container legal-grid">

                    {/* Desktop Navigation Sidebar */}
                    <aside className="legal-sidebar">
                        <div className="sidebar-sticky">
                            <p className="sidebar-title">Contents</p>
                            <nav>
                                <ul>
                                    {navItems.map(item => (
                                        <li key={item.id}>
                                            <a href={`#${item.id}`}>{item.label}</a>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </div>
                    </aside>

                    {/* Main Policy Document */}
                    <div className="legal-document">
                        <div className="policy-intro">
                            <p>
                                Welcome to <strong>Deal To Close (AI)</strong> (“Deal To Close,” “we,” “our,” or “us”). We are committed to protecting the privacy, confidentiality, and security of the information entrusted to us. This Privacy Policy explains how we collect, use, store, process, and protect personal and business information when you access our website, applications, integrations, and related services (collectively, the “Services”).
                            </p>
                            <p>
                                This policy is designed to meet the expectations and requirements of major platform providers including
                                <strong> Google, Microsoft</strong>, and other third-party integration partners.
                            </p>
                        </div>

                        <hr className="section-divider" />

                        <h2 id="info-collect">1. Information We Collect</h2>
                        <h3>1.1 Information You Provide</h3>
                        <p>
                            When you create an account, you may provide information such as your name, email address, phone number, company details, login credentials, and billing information. You may also upload contracts, transaction documents, and forms necessary to manage your deals.
                        </p>
                        <h3>1.2 Information from Connected Services</h3>
                        <p>If you connect third-party services, we access limited data strictly required for functionality:</p>
                        <ul className="legal-list">
                            <li>Gmail and Outlook email content and metadata</li>
                            <li>Google and Outlook contacts and calendar data</li>
                            <li>Location or address data from mapping services</li>
                            <li>Other authorized Google Workspace or Microsoft 365 data</li>
                        </ul>

                        <h2 id="how-use">2. How We Use Information</h2>
                        <p>We use information <strong>solely</strong> to:</p>
                        <ul className="legal-list">
                            <li>Automate transaction workflows and document organization</li>
                            <li>Extract key information from uploaded contracts</li>
                            <li>Generate tasks, due dates, and reminders</li>
                            <li>Enable CRM, email, and calendar integrations</li>
                            <li>Maintain platform security and reliability</li>
                        </ul>
                        <div className="highlight-box">
                            <strong>Note:</strong> We do <strong>not</strong> sell user data or use it for advertising purposes.
                        </div>

                        <h2 id="api-compliance">3. Google & Microsoft API Compliance</h2>
                        <p>
                            Our use of information obtained through Google APIs complies with the <strong>Google API Services User Data Policy</strong>, including the Limited Use requirements. Data is not sold, shared, or used for advertising. Access is limited to the minimum required scopes.
                        </p>

                        <h2 id="data-sharing">4. Data Sharing</h2>
                        <p>We share data only with:</p>
                        <ul className="legal-list">
                            <li>Authorized service providers supporting hosting, security, or communications</li>
                            <li>Third-party platforms you explicitly connect</li>
                            <li>Legal authorities when required by law</li>
                        </ul>

                        <h2 id="data-retention">5. Data Retention</h2>
                        <p>We retain data only for as long as necessary to provide the Services or comply with legal requirements. Users may request deletion of their data at any time.</p>

                        <h2 id="security">6. Security Measures</h2>
                        <p>Deal To Close (AI) uses industry-standard safeguards including encryption, secure cloud infrastructure, and access controls to protect data. While no system is risk-free, we continuously improve our security posture.</p>

                        <h2 id="user-rights">7. User Rights</h2>
                        <p>Depending on your jurisdiction, you may have the right to access, correct, delete, or restrict the processing of your personal data. Requests can be submitted through our support channels.</p>

                        <h2 id="intl-transfers">8. International Data Transfers</h2>
                        <p>Deal To Close (AI) operates globally. Your data may be processed in different jurisdictions with appropriate safeguards (such as Standard Contractual Clauses) in place.</p>

                        <h2 id="policy-changes">9. Changes to This Policy</h2>
                        <p>We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date. Significant changes will be communicated via email or platform notification.</p>

                        <h2 id="contact">10. Contact Us</h2>
                        <div className="contact-card">
                            <p>If you have questions about this Privacy Policy, please contact us at:</p>
                            <div className="contact-details">
                                <strong>Deal to Close AI</strong><br />
                                <span>Email: </span>
                                <a href="mailto:contact@dealtoclose.ai">contact@dealtoclose.ai</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PrivacyPolicy;